"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import Link from "@/components/LocaleLink";
import { ArrowRight, Clock } from "lucide-react";
import { WithAmp } from "@/components/WithAmp";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import type { Locale } from "@/lib/i18n/translations";

/** The article data says "4 Min" (German); the other languages get their own unit. */
function localReadTime(readTime: string, locale: Locale): string {
  const minutes = parseInt(readTime, 10);
  if (!Number.isFinite(minutes)) return readTime;
  const unit: Record<Locale, string> = { de: "Min", en: "min", da: "min", ar: minutes >= 3 && minutes <= 10 ? "دقائق" : "دقيقة", uk: "хв", ru: "мин", vi: "phút", es: "min" };
  return `${minutes}\u00a0${unit[locale]}`;
}

export type ArticleCardData = {
  slug: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  image?: string;
};

type Props = {
  article: ArticleCardData;
  /** "feature": the large cards at the top of the hub. "grid": the standard
   *  card, which is a compact row with a thumbnail on a phone. */
  variant?: "feature" | "grid";
  readLabel: ReactNode;
  /** Small label in the meta line, e.g. "Zum Einstieg". */
  badge?: ReactNode;
  headingLevel?: "h2" | "h3";
  /** Hidden via CSS (not removed), so every link stays in the server HTML. */
  hidden?: boolean;
  /** Only for the hub's first card: its photo is the page's largest paint,
   *  so it loads at once and first instead of lazily. */
  priority?: boolean;
  className?: string;
};

/**
 * One guide card for the hub and for "Weitere Ratgeber-Artikel" under each
 * article, in the home's card language: white, rounded-lg, hairline border,
 * soft shadow; wine text link. The photo is shown whole on a blurred copy of
 * itself, like the heroes — a crop cut heads off in portrait photos — and the
 * category sits in the meta line instead of on someone's face.
 */
export function ArticleCard({
  article,
  variant = "grid",
  readLabel,
  badge,
  headingLevel = "h3",
  hidden = false,
  priority = false,
  className,
}: Props) {
  const { locale } = useTranslation();
  const feature = variant === "feature";
  const Heading = headingLevel;
  // next/image's `priority` is deprecated since Next 16; eager + high fetch
  // priority is what the docs recommend for an LCP image.
  const loadProps = priority ? ({ loading: "eager", fetchPriority: "high" } as const) : {};
  return (
    <Link
      href={`/ratgeber/${article.slug}`}
      className={cn(
        "group flex overflow-hidden rounded-lg border border-navy-100 bg-white shadow-[var(--shadow-soft)] transition duration-300 hover:-translate-y-0.5 hover:border-navy-200 hover:shadow-[var(--shadow-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral-300 focus-visible:ring-offset-2",
        feature ? "flex-col" : "flex-row sm:flex-col",
        hidden && "hidden",
        className,
      )}
    >
      <div
        className={cn(
          "relative shrink-0 overflow-hidden bg-cream-100",
          // Phone list: an inset landscape thumbnail, vertically centred —
          // stretched to the card's height a landscape photo shrank to a thin
          // strip between blurred bands.
          feature
            ? "aspect-[3/2] w-full"
            : // 3:2 at every width: the guide photos are 3:2, and a 16:10 card
              // left blurred slivers on both sides that looked like a broken crop.
              "ms-3 aspect-[3/2] w-28 self-center rounded-md sm:ms-0 sm:w-full sm:self-auto sm:rounded-none",
        )}
      >
        {article.image ? (
          <>
            {/* Identical props, so both layers use one download. */}
            <Image
              src={article.image}
              alt=""
              aria-hidden
              fill
              sizes={feature ? "(min-width: 640px) 50vw, 100vw" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 112px"}
              {...loadProps}
              className="scale-110 object-cover blur-[18px] brightness-[0.85]"
            />
            <Image
              src={article.image}
              alt=""
              fill
              sizes={feature ? "(min-width: 640px) 50vw, 100vw" : "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 112px"}
              {...loadProps}
              className="object-contain transition-transform duration-700 group-hover:scale-[1.03]"
            />
          </>
        ) : null}
      </div>

      <div className={cn("flex min-w-0 flex-1 flex-col", feature ? "p-5 sm:p-6" : "px-4 py-3.5 sm:p-5")}>
        {/* No separator dot: the clock icon already separates category and
            read time, and a dot was left dangling when a long category
            ("Visum und Aufenthalt") pushed the time onto a second line. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] leading-5 text-navy-500">
          <span className="font-medium text-navy-600">
            <WithAmp text={article.category} />
          </span>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            <bdi>{localReadTime(article.readTime, locale)}</bdi>
          </span>
          {badge}
        </div>
        <Heading
          className={cn(
            "mt-2 text-balance font-display font-normal leading-[1.28] [hyphens:manual] [overflow-wrap:anywhere] text-ink-900 transition-colors group-hover:text-coral-700",
            feature ? "text-[1.3rem] sm:text-[1.55rem]" : "text-[16.5px] sm:text-[19px]",
          )}
        >
          {article.title}
        </Heading>
        <p
          className={cn(
            "mt-2 line-clamp-2 leading-[1.6] text-navy-600",
            feature ? "text-[14.5px]" : "text-[14px] max-sm:hidden",
          )}
        >
          {article.description}
        </p>
        <span
          className={cn(
            "mt-auto inline-flex items-center gap-1.5 pt-4 text-[13.5px] font-semibold text-coral-700",
            !feature && "max-sm:hidden",
          )}
        >
          {readLabel}
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
            strokeWidth={2}
            aria-hidden
          />
        </span>
      </div>
    </Link>
  );
}
